// * generally want to limit the imports of corso-types but this component is built specifically for a type from corso-types
import { CrewUi } from 'corso-types';
import { match } from 'ts-pattern';
import Input from './form-controls/input';
import { MultiSelect } from './form-controls/multi-select';
import { Select, SelectItem } from './form-controls/select';

type CustomField =
  CrewUi.CollectCustomFieldEvent['params']['customFields'][number];

const stringArrayDefaultValue = (defaultValue?: string): string[] => {
  if (!defaultValue) return [];

  return defaultValue
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
};

function CustomFieldValue({
  field: customField,
  defaultValue,
  className,
}: {
  field: CustomField;
  defaultValue?: string;
  className?: string;
}) {
  return match(customField)
    .with({ valueType: 'MultiSelect' }, (field) => (
      <MultiSelect
        id={`${field.id}`}
        name={`${field.id}[value]`}
        label={field.displayName}
        className={className}
        required={field.required ?? true}
        description={field.description}
        defaultValue={stringArrayDefaultValue(defaultValue)}
        options={field.options.map((option) => ({
          label: option,
          value: option,
        }))}
      />
    ))
    .with({ valueType: 'Select' }, (field) => (
      <Select
        id={`${field.id}`}
        className={className}
        required={field.required ?? true}
        description={field.description}
        name={`${field.id}[value]`}
        label={field.displayName}
        defaultValue={defaultValue}
      >
        {field.options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </Select>
    ))
    .with({ valueType: 'Text' }, (field) => (
      <Input
        type="text"
        id={`${field.id}`}
        className={className}
        required={field.required ?? true}
        description={field.description}
        name={`${field.id}[value]`}
        label={field.displayName}
        defaultValue={defaultValue}
      />
    ))
    .with({ valueType: 'Date' }, (field) => (
      <Input
        type="date"
        id={`${field.id}`}
        className={className}
        required={field.required ?? true}
        description={field.description}
        name={`${field.id}[value]`}
        label={field.displayName}
        defaultValue={defaultValue}
      />
    ))
    .with({ valueType: 'Number' }, (field) => (
      <Input
        type="number"
        id={`${field.id}`}
        className={className}
        required={field.required ?? true}
        description={field.description}
        name={`${field.id}[value]`}
        label={field.displayName}
        defaultValue={defaultValue}
      />
    ))
    .exhaustive();
}

function CustomFieldType({ field }: { field: CustomField }) {
  return (
    <input
      type="hidden"
      name={`${field.id}[type]`}
      value={field.valueType}
      readOnly
    />
  );
}

export default function CustomField({
  field,
  defaultValue,
  className,
}: {
  field: CustomField;
  defaultValue?: string;
  className?: string;
}) {
  return (
    <>
      <CustomFieldValue
        field={field}
        defaultValue={defaultValue}
        className={className}
      />
      <CustomFieldType field={field} />
    </>
  );
}
